/**
 * Per-series configuration for the workshop scarcity-display feature.
 *
 * Origin: docs/brainstorms/2026-05-26-workshop-scarcity-display-requirements.md
 * Plan:   docs/plans/2026-05-26-001-feat-workshop-scarcity-display-plan.md
 *
 * The seat clock is a deterministic, TIME-driven counter (not real-bookings driven).
 * Every visitor sees the same count for the same workshop at the same moment, with a
 * predictable 6-hour tick cadence. See `useSeatClock` for the math.
 *
 * Invariant: a series's `startingCount` MUST be ≤ the minimum `maxCapacity` across
 * all real events in that series. Otherwise the page will display more "seats left"
 * than the real cohort can hold, producing an observable contradiction when admin
 * flips a real session to sold_out on booking #(maxCapacity). The hook also caps
 * the displayed count at `event.maxCapacity` at render time as a runtime guard, but
 * config authors should respect the invariant up front.
 *
 * Enabled today: Cowork, Design, Code and Sales. The remaining entries live
 * commented for forward reference but are NOT consumed until the staged-rollout
 * decision lands. See the plan's "Deferred to Follow-Up Work" section.
 */

import type { SeatCurvePoint, DayBeforeRamp } from "@/components/scarcity/useSeatClock";

export type SeriesScarcityConfig = {
  /**
   * METER SCALE only — the denominator for the bar fill level and the
   * SSR-neutral count. This is NOT the displayed seat number anymore (that
   * comes from `curve` / `dayBefore`). Tune it to control when the scarcity
   * badge starts firing: the badge shows once the displayed count drops to
   * ≤ 50% of this (e.g. scale 24 → badge appears at ≤ 12 seats). Keep it
   * ≥ the curve's largest `seats` so a wide-open session still reads "open".
   */
  startingCount: number;
  /**
   * The displayed seat count as a function of days-to-workshop, given as
   * `{daysOut, seats}` control points sorted DESCENDING by `daysOut`. The count
   * interpolates linearly between adjacent points; beyond the outermost point
   * it's flat at that point's seats. Drives everything up to the day-before
   * window — the final day is owned by `dayBefore`.
   */
  curve: SeatCurvePoint[];
  /**
   * Wall-clock ramp for the day before the workshop (anchored to the ET clock,
   * not hours-until-start): `startSeats` at midnight ET decaying linearly to
   * `eveningSeats` by `eveningHourET`, then held at `eveningSeats` through
   * workshop start. `eveningSeats` is the effective floor.
   */
  dayBefore: DayBeforeRamp;
  /**
   * Synthetic past dates used only when the series has fewer than 2 real past
   * sessions. Each entry renders as a "Sold Out" tile identical to a real one,
   * but is excluded from sitemaps, SEO, and any structured-data emission.
   * Dates are ISO strings parsed in America/New_York.
   */
  syntheticPast?: { date: string; ariaLabel: string }[];
};

/**
 * Currently enabled series. Add an entry to enable scarcity-display on a series.
 * v1 shipped Cowork alone; Design, Code and Sales were added in later passes.
 */
export const SCARCITY_CONFIG: Record<string, SeriesScarcityConfig> = {
  "claude-cowork-for-beginners-workshop": {
    // Meter scale only (see type doc). 24 → the "Only N left" badge starts
    // firing at ≤ 12 seats, i.e. ~2 days out (11) but not a week out (15).
    startingCount: 24,
    // Displayed count by days-out. Targets: ~11 two days out, 9 the day before.
    // Interpolated between points; flat at 15 beyond a week out. 2026-06-24:
    // replaced the single linear decay-to-1, which read as fake ("1 seat left"
    // the day before). Floor is now 3, via `dayBefore.eveningSeats`.
    curve: [
      { daysOut: 7, seats: 15 },
      { daysOut: 2, seats: 11 },
      { daysOut: 1, seats: 9 },
    ],
    // Day before: 9 at midnight ET → 3 by 8:00 PM ET, then held at 3 to start.
    dayBefore: { startSeats: 9, eveningSeats: 3, eveningHourET: 20 },
  },

  // ⚠️ Claude Design synthetic scarcity ENABLED per explicit owner decision
  // (Esther, 2026-07-29) for INTERNAL use, mirroring Cowork's live treatment.
  // NOTE: this is the same FTC / trust-risk pattern the June audit flagged (§1.4)
  // and that the code deferred pending sign-off. Keep INTERNAL and get Isaac's
  // FTC/legal sign-off before promoting the Design page to public traffic.
  "claude-for-design-workshop": {
    startingCount: 18,
    curve: [
      { daysOut: 7, seats: 12 },
      { daysOut: 2, seats: 9 },
      { daysOut: 1, seats: 7 },
    ],
    dayBefore: { startSeats: 7, eveningSeats: 3, eveningHourET: 20 },
  },

  // Second series enabled in the staged rollout, mirroring Cowork's curve and
  // ramp exactly (same startingCount, same control points) so the two pages
  // read consistently. Cowork has shipped this display since v1.
  //
  // NOTE: this event carries no `maxCapacity`, so the hook's render-time cap
  // at real capacity is a no-op here — Cowork is backstopped by its 100. The
  // curve tops out at the 80% display ceiling (19) either way, but setting a
  // capacity on the event in /admin would restore that second guard.
  "claude-code-for-beginners-workshop": {
    startingCount: 24,
    curve: [
      { daysOut: 7, seats: 15 },
      { daysOut: 2, seats: 11 },
      { daysOut: 1, seats: 9 },
    ],
    dayBefore: { startSeats: 9, eveningSeats: 3, eveningHourET: 20 },
  },

  // Claude for Sales Teams (Isaac, 2026-09-09 review): same clock as Cowork so
  // the two pages read consistently. The series has no real past session
  // (its only earlier row, Jun 3 2026, was cancelled), so ONE synthetic
  // sold-out date fills the past slot: Tuesday, September 8, 2026, one week
  // before the first real date. Written with an explicit ET offset because
  // deriveTimelineTiles parses this with `new Date()`, and a bare YYYY-MM-DD
  // would be read as UTC midnight, which is the previous evening in New York.
  // Esther had deliberately left this page without a clock (FTC caution,
  // see event-detail.tsx); Isaac asked for it explicitly on 2026-09-09.
  // maxCapacity on the real rows is 75, so the startingCount invariant holds.
  "claude-for-sales-teams-workshop": {
    startingCount: 24,
    curve: [
      { daysOut: 7, seats: 15 },
      { daysOut: 2, seats: 11 },
      { daysOut: 1, seats: 9 },
    ],
    dayBefore: { startSeats: 9, eveningSeats: 3, eveningHourET: 20 },
    syntheticPast: [
      { date: "2026-09-08T10:00:00-04:00", ariaLabel: "Past session, Tuesday, September 8. Sold out." },
    ],
  },

  // ── Forward-reference only (NOT enabled in v1) ──────────────────────────
  // Uncomment after the staged-rollout decision per the origin's deferred
  // "Staged-rollout gate" question. Both require the deferred trust-risk and
  // FTC-review questions to be resolved first.
  //
  // "claude-code-beginners": {
  //   startingCount: 24,
  //   curve: [
  //     { daysOut: 7, seats: 15 },
  //     { daysOut: 2, seats: 11 },
  //     { daysOut: 1, seats: 9 },
  //   ],
  //   dayBefore: { startSeats: 9, eveningSeats: 3, eveningHourET: 20 },
  // },
  // "claude-design-workshop": {
  //   startingCount: 18,
  //   curve: [
  //     { daysOut: 7, seats: 12 },
  //     { daysOut: 2, seats: 9 },
  //     { daysOut: 1, seats: 7 },
  //   ],
  //   dayBefore: { startSeats: 7, eveningSeats: 3, eveningHourET: 20 },
  //   syntheticPast: [
  //     { date: "2026-04-23", ariaLabel: "Past session — sold out" },
  //   ],
  // },
};

/** Returns the scarcity config for a series, or `null` when the series is not enabled. */
export function getScarcityConfig(seriesSlug: string | null | undefined): SeriesScarcityConfig | null {
  if (!seriesSlug) return null;
  return SCARCITY_CONFIG[seriesSlug] ?? null;
}
